"use client";
import React, { useState } from "react";
import {
    AccordionDetails,
    Checkbox,
    FormControlLabel,
    AccordionProps,
    AccordionSummaryProps,
    styled,
    TextField,
    Button,
    Box,
    Radio,
    RadioGroup,
    IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary from "@mui/material/AccordionSummary";

type Filter = {
    name: string;
};

interface FilterProps {
    onClose?: () => void; // ⭐ optional close function for drawer
}

const Accordion = styled((props: AccordionProps) => (
    <MuiAccordion disableGutters elevation={0} square {...props} />
))({});

const AccordionSummary = styled((props: AccordionSummaryProps) => (
    <MuiAccordionSummary
        expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />}
        {...props}
    />
))({});

function ProductsFilter({ onClose }: FilterProps) {
    const [expandedPanels, setExpandedPanels] = useState<Record<string, boolean>>({
        categories: true,
        size: true,
        brand: true,
        colour: true,
        price: true,
        rating: true,
    });

    const togglePanel = (panel: string) => {
        setExpandedPanels((prev) => ({
            ...prev,
            [panel]: !prev[panel],
        }));
    };

    const categories: Filter[] = [
        { name: "Electronics" },
        { name: "Fashion" },
        { name: "Beauty" },
        { name: "Furniture" },
        { name: "Accessories" },
        { name: "Jewellery" },
        { name: "Footwear" },
        { name: "Home & Living" },
        { name: "Toys" },
    ];

    const sizes = [{ name: "07" }, { name: "08" }, { name: "09" }, { name: "10" }];

    const brands: Filter[] = [
        { name: "Nike" },
        { name: "Adidas" },
        { name: "Puma" },
        { name: "Reebok" },
        { name: "Skechers" },
    ];
    const [filterOpen, setFilterOpen] = useState(false); // drawer state

    const colours = [
        { name: "Red", code: "#C4555A", qty: "200" },
        { name: "Black", code: "#000000", qty: "300" },
        { name: "Navy Blue", code: "#1A2D57", qty: "163" },
        { name: "Grey", code: "#7A7A7A", qty: "150" },
        { name: "Brown", code: "#7B4B25", qty: "100" },
        { name: "White", code: "#FFFFFF", qty: "500" },
    ];

    const ratings = [
        { value: 5, label: "5.0" },
        { value: 4, label: "4.0" },
        { value: 3, label: "3.0" },
        { value: 2, label: "2.0" },
    ];

    return (
        <div className="filter_aside">
            {/* ⭐ Header with close button */}
            <div className="filter_head">
                <h2 className="hd_4">Filters</h2>

            </div>

            <div className="accordion_list">
                {/* Categories */}
                <Accordion expanded={expandedPanels.categories} onChange={() => togglePanel("categories")}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>Categories</AccordionSummary>
                    <AccordionDetails>
                        <RadioGroup className="checkbox_list" name="categories">
                            {categories.map((category, idx) => (
                                <FormControlLabel
                                    key={idx}
                                    value={category.name}
                                    control={<Radio size="small" />}
                                    label={category.name}
                                />
                            ))}
                        </RadioGroup>
                    </AccordionDetails>
                </Accordion>

                {/* Sizes */}
                <Accordion expanded={expandedPanels.size} onChange={() => togglePanel("size")}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>Size</AccordionSummary>
                    <AccordionDetails>
                        <div className="checkbox_list">
                            {sizes.map((size, idx) => (
                                <FormControlLabel key={idx} control={<Checkbox size="small" />} label={size.name} />
                            ))}
                        </div>
                    </AccordionDetails>
                </Accordion>

                {/* Brands */}
                <Accordion expanded={expandedPanels.brand} onChange={() => togglePanel("brand")}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>Brands</AccordionSummary>
                    <AccordionDetails>
                        <div className="checkbox_list">
                            {brands.map((brand, idx) => (
                                <FormControlLabel key={idx} control={<Checkbox size="small" />} label={brand.name} />
                            ))}
                        </div>
                    </AccordionDetails>
                </Accordion>

                {/* Colours */}
                <Accordion expanded={expandedPanels.colour} onChange={() => togglePanel("colour")}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>Colours</AccordionSummary>
                    <AccordionDetails>
                        <div className="checkbox_list">
                            {colours.map((colour, idx) => (
                                <FormControlLabel
                                    key={idx}
                                    control={<Checkbox size="small" />}
                                    label={
                                        <>
                                            <span className="color_dot" style={{ backgroundColor: colour.code }}></span>
                                            {colour.name} <span>({colour.qty})</span>
                                        </>
                                    }
                                />
                            ))}
                        </div>
                    </AccordionDetails>
                </Accordion>

                {/* Price */}
                <Accordion expanded={expandedPanels.price} onChange={() => togglePanel("price")}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>Price ($)</AccordionSummary>
                    <AccordionDetails>
                        <div className="form price_form">
                            <TextField type="number" placeholder="From" fullWidth hiddenLabel />
                            <span>TO</span>
                            <TextField type="number" placeholder="To" fullWidth hiddenLabel />
                            <Button>GO</Button>
                        </div>
                    </AccordionDetails>
                </Accordion>

                {/* Ratings */}
                <Accordion expanded={expandedPanels.rating} onChange={() => togglePanel("rating")}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>Ratings</AccordionSummary>
                    <AccordionDetails>
                        <RadioGroup className="checkbox_list" name="ratings">
                            {ratings.map((rating, idx) => (
                                <FormControlLabel
                                    key={idx}
                                    value={rating.value}
                                    control={<Radio size="small" />}
                                    label={
                                        <>
                                            <div className="stars">
                                                {"★".repeat(rating.value)}
                                                <span className="unfilled_star">{"★".repeat(5 - rating.value)}</span>
                                            </div>
                                            {rating.label}
                                        </>
                                    }
                                />
                            ))}
                        </RadioGroup>
                    </AccordionDetails>
                </Accordion>
                <div className="btn_flx">
                    <Button className="btn btn_primary">Apply</Button>
                </div>
            </div>
        </div>
    );
}

export default ProductsFilter;
